import {FormGroup} from '@angular/forms';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';

import {
    AmsService,
    ApiError,
    ApiResponse,
    CompanySummaryInterface,
    HttpStatusCodesEnum,
    MessageTemplateInputModel,
    MessageTemplateInterface,
    MessageTemplateModel,
    MessagingDistributionChannelsEnum,
    MessagingStatusesEnum,
    MessagingTemplateTypesEnum
} from '@spa/api';

import {
    MessageTemplateBodySelector,
    MessageTemplateChannelEmailSelector,
    MessageTemplateChannelPullSelector,
    MessageTemplateChannelPushSelector,
    MessageTemplateCompanySelector,
    MessageTemplateDepartmentSelector,
    MessageTemplateDistributionChannelsSelector,
    MessageTemplateImportanceLevelSelector,
    MessageTemplateKeyCodeSelector,
    MessageTemplateKeyDescriptionSelector,
    MessageTemplatePushNotificationBodySelector,
    MessageTemplateStatusSelector,
    MessageTemplateSubjectSelector,
    MessageTemplateTemplateNameSelector,
    MessageTemplateTemplateTypeSelector,
} from 'appAssets/selectors';
import {CheckboxControl, Helper} from 'appShared/classes';
import {PanelSettingsMessagingResolverErrorStatesEnum} from 'appEntries/panel/enums';
import {MessageTemplatesControls, MessageTemplatesControlsInterface} from 'appAssets/descriptors';
import {BaseMessageTemplatesForm} from './panel-settings-base-messaging-templates.form';
import {AmsPermissionService} from 'appEntries/panel/services';
import {AutocompleteOptionInterface} from 'appShared/components';

type ControlValueType = string | number | boolean | string[];

export class MessageTemplatesForm extends BaseMessageTemplatesForm<MessageTemplatesControlsInterface> {

    constructor(
        private readonly amsPermissionService: AmsPermissionService,
        protected readonly amsService: AmsService,
        protected readonly companies: CompanySummaryInterface[],
        protected readonly messageTemplates: MessageTemplateInterface[],
        public templateModel: MessageTemplateModel = null,
        protected readonly departments: string[],
    ) {
        super(amsService, companies, templateModel, departments);
        this.hasApprovePermission = amsPermissionService.hasApproveMessageTemplate();
    }

    private getImportanceLevel(): number {
        return this.form.value[MessageTemplateImportanceLevelSelector] === true ? 1 : 0;
    }

    private getTemplateNameByKeyCode(keyCodeValue: string): string {
        const selectedTemplate = this.messageTemplates?.find(template => template.keyCode === keyCodeValue);
        if (selectedTemplate) {
            return selectedTemplate.name;
        }

        return '';
    }

    private getTemplateName(isUpdateForm: boolean): string {
        let templateName = '';
        if (isUpdateForm) {
            templateName = this.templateModel.customerId
                ? this.getTemplateNameByKeyCode(this.templateModel.keyCode)
                : this.form.value[MessageTemplateTemplateNameSelector];

            return templateName;
        }
        templateName = this.form.value[MessageTemplateTemplateTypeSelector] === MessagingTemplateTypesEnum.New ?
            this.form.value[MessageTemplateTemplateNameSelector] :
            this.getTemplateNameByKeyCode(this.form.value[MessageTemplateKeyCodeSelector]);

        return templateName;
    }

    private getDistributionChannels(): MessagingDistributionChannelsEnum[] {
        const result: MessagingDistributionChannelsEnum[] = [];

        if (this.controls.channelEmail.getChecked()) {
            result.push(MessagingDistributionChannelsEnum.Email);
        }
        if (this.controls.channelPush.getChecked()) {
            result.push(MessagingDistributionChannelsEnum.Push);
        }
        if (this.controls.channelPull.getChecked()) {
            result.push(MessagingDistributionChannelsEnum.Pull);
        }

        return result;
    }

    private getSelectedTemplate() {
        return this.messageTemplates?.find(template => template.keyCode === this.form.value[MessageTemplateKeyCodeSelector]);
    }

    private getDepartment(isUpdateForm): string {

        if (isUpdateForm) {
            return this.templateModel.department;
        }

        if (this.form.value[MessageTemplateTemplateTypeSelector] === MessagingTemplateTypesEnum.Additional) {
            return this.getSelectedTemplate()?.department;
        }

        return this.form.value[MessageTemplateDepartmentSelector];
    }

    private getPushNotificationBody(isUpdateForm: boolean): string {
        if (this.form.value[MessageTemplateChannelPushSelector] !== null) {
            return this.form.value[MessageTemplatePushNotificationBodySelector];
        }

        if (isUpdateForm) {
            return (this.templateModel.distributionChannels.indexOf(MessagingDistributionChannelsEnum.Push.toString()) >= 0) ?
                this.form.value[MessageTemplatePushNotificationBodySelector] : null;
        }

        return null;
    }

    private getMessageTemplateInputModel(isUpdateForm = false): MessageTemplateInputModel {
        return new MessageTemplateInputModel(
            this.getTemplateName(isUpdateForm),
            this.getDepartment(isUpdateForm),
            this.form.value[MessageTemplateStatusSelector],
            isUpdateForm ? this.templateModel.customerId : this.form.value[MessageTemplateCompanySelector],
            this.form.value[MessageTemplateSubjectSelector],
            this.form.value[MessageTemplateBodySelector],
            this.getImportanceLevel(),
            false,
            isUpdateForm ? this.templateModel.keyCode : this.form.value[MessageTemplateKeyCodeSelector],
            null,
            isUpdateForm ? this.templateModel.keyDescription : this.form.value[MessageTemplateKeyDescriptionSelector],
            this.getDistributionChannels(),
            this.getPushNotificationBody(isUpdateForm),
        );
    }

    private updateMessageTemplate(): Observable<ApiResponse<ApiError[] | boolean>> {
        const triggerInputModel = this.getMessageTemplateInputModel(true);

        return this.amsService.updateMessageTemplate(this.templateModel.id, triggerInputModel)
            .pipe(
                map((response) => {
                    if (response.status !== HttpStatusCodesEnum.Ok) {
                        return Helper.errorsHelper().createErrorResponse(
                            response,
                            PanelSettingsMessagingResolverErrorStatesEnum.UpdateMessageTemplateFailed
                        );
                    }

                    const messageTemplateData = response.data as MessageTemplateInterface;
                    this.templateModel = new MessageTemplateModel(messageTemplateData);

                    return Helper.createSuccessResponse<boolean>(true);
                })
            );
    }

    private createMessageTemplate(): Observable<ApiResponse<ApiError[] | boolean>> {
        const triggerInputModel = this.getMessageTemplateInputModel();

        return this.amsService.createMessageTemplate(triggerInputModel)
            .pipe(
                map(response => {
                    if (response.status !== HttpStatusCodesEnum.Ok) {
                        return Helper.errorsHelper().createErrorResponse(
                            response,
                            PanelSettingsMessagingResolverErrorStatesEnum.CreateMessageTemplateFailed
                        );
                    }

                    const messageTemplateData = response.data as MessageTemplateInterface;
                    this.templateModel = new MessageTemplateModel(messageTemplateData);

                    return Helper.createSuccessResponse<boolean>(true);
                })
            );
    }

    private setDistributionChannel(control: CheckboxControl, channel: MessagingDistributionChannelsEnum) {
        if (control && this.templateModel.distributionChannels.indexOf(channel) >= 0) {
            control.value = true;
            control.setChecked(true);
        }
    }

    protected prepareMessageTemplatesOptions(): AutocompleteOptionInterface[] {
        if (!this.messageTemplates || this.messageTemplates.length === 0) {
            return [];
        }

        return this.messageTemplates.map((item: MessageTemplateInterface): AutocompleteOptionInterface => {
            return {
                label: item.name,
                value: item.keyCode
            };
        });
    }

    getControlValue(name: string): ControlValueType {
        switch (name) {
            case MessageTemplateTemplateNameSelector:
                return this.templateModel.name;
            case MessageTemplateStatusSelector:
                return this.templateModel.status;
            case MessageTemplateSubjectSelector:
                return this.templateModel.subject;
            case MessageTemplateBodySelector:
                return this.templateModel.body;
            case MessageTemplateImportanceLevelSelector:
                return this.templateModel.importanceLevel;
            case MessageTemplateDistributionChannelsSelector:
                return this.templateModel.distributionChannels;
            case MessageTemplateTemplateTypeSelector:
                return this.templateModel.templateType;
            case MessageTemplateCompanySelector:
                return this.templateModel.customerId;
            case MessageTemplateDepartmentSelector:
                return this.templateModel.department;
            case MessageTemplatePushNotificationBodySelector:
                return this.templateModel.pushNotificationBody;
            case MessageTemplateChannelEmailSelector:
                return (this.templateModel.distributionChannels.indexOf(MessagingDistributionChannelsEnum.Email) >= 0);
            case MessageTemplateChannelPushSelector:
                return (this.templateModel.distributionChannels.indexOf(MessagingDistributionChannelsEnum.Push) >= 0);
            case MessageTemplateChannelPullSelector:
                return (this.templateModel.distributionChannels.indexOf(MessagingDistributionChannelsEnum.Pull) >= 0);
        }

        return null;
    }

    initForm(): void {
        this.form = new FormGroup({});

        this.controls = MessageTemplatesControls(!this.templateModel, this.hasApprovePermission);

        if (!this.templateModel) {
            this.controls.status.value = MessagingStatusesEnum.PendingApproval;
            this.controls.status.disabled = true;
            this.controls.templateType.value = MessagingTemplateTypesEnum.New;
        } else {
            this.controls.status.value = this.templateModel.status;
            this.controls.status.options.map(option => {
                option.checked = option.value === this.templateModel.status;
            });

            this.controls.importanceLevel.value = this.templateModel.importanceLevel;
            this.controls.importanceLevel.setChecked(this.templateModel.importanceLevel === 1);

            this.setDistributionChannel(this.controls.channelEmail, MessagingDistributionChannelsEnum.Email);
            this.setDistributionChannel(this.controls.channelPush, MessagingDistributionChannelsEnum.Push);
            this.setDistributionChannel(this.controls.channelPull, MessagingDistributionChannelsEnum.Pull);

            this.controls.pushNotificationBody.value = this.templateModel.pushNotificationBody;

            /**
             * Validators are reset, because in edit mode name cannot be changed.
             */
            this.controls.name.resetValidators();
        }

        this.controls.company.options = this.prepareCompaniesOptions();
        this.controls.keyCode.options = this.prepareMessageTemplatesOptions();
        this.controls.department.options = this.prepareDepartmentsOptions();

        this.prepareFormValue();
    }

    createRequest(): Observable<ApiResponse<ApiError[] | boolean>> {
        if (!this.templateModel) {
            return this.createMessageTemplate();
        }

        return this.updateMessageTemplate();
    }

}
